import type {
  SubmitRecordInput,
  RecordCheckpointScanInput,
  SkipDueInput,
  RaiseRequestInput,
  MarkAttendanceRollInput,
  StartRequestInput,
  MarkRequestDoneInput,
  SaveDprDraftInput,
  CreateConIssueInput,
} from '../../types/ops'
import type { SaveBmwDayInput } from '../hosBmwService'

export type HelperMutationStatus = 'PENDING' | 'SYNCING' | 'FAILED' | 'COMPLETED' | 'ACCESS_LOST' | 'EXPIRED'

export type HelperMutationType =
  | 'CHECKLIST_TOGGLE'
  | 'CHECKLIST_PROOF_UPLOAD'
  | 'CHECKLIST_COMMENT'
  | 'CHECKLIST_VOICE_MEMO'
  | 'WORKSPACE_ACTIVITY'
  | 'STOCK_COUNT_ROUND'
  | 'PENDING_EXPENSE_INTAKE'
  | 'STOCK_SCAN_SUGGESTION'
  | 'EDU_BOARDING'
  | 'EDU_ATTENDANCE_MARK'
  | 'TIME_CLOCK'
  | 'OPS_DUE_RECORD'
  | 'OPS_CHECKPOINT_SCAN'
  | 'OPS_SKIP_DUE'
  | 'OPS_RAISE_REQUEST'
  | 'OPS_ATTENDANCE_ROLL'
  | 'OPS_START_REQUEST'
  | 'OPS_MARK_REQUEST_DONE'
  | 'OPS_BMW_SAVE'
  | 'OPS_DPR_SAVE'
  | 'OPS_CON_ISSUE_CREATE'
  | 'OPS_CON_ISSUE_ATTACH_PHOTOS'

export type HelperChecklistWorkflowStatus =
  | 'ASSIGNED'
  | 'ACCEPTED'
  | 'ASSESSING'
  | 'ONGOING'
  | 'COMPLETED'
  | 'NOT_REQUIRED'
  | 'POSTPONED'

/**
 * Which shell enqueued this mutation. Drives two runtime decisions in the
 * sync worker:
 *   1. Visibility guard — MEMBER writes bypass `assertHelperCanAccessItem`
 *      (helpers-only masking should not block admins/members).
 *   2. Audit stamping — the audit ledger row's `actorRole` is stamped from
 *      this value; without it every mobile write shows up as HELPER.
 *
 * Optional so existing HELPER-only call sites (HelperDashboardLayout, etc.)
 * keep working unchanged; absent → 'HELPER'.
 */
export type MobileOutboxActorRole = 'HELPER' | 'MEMBER'

/**
 * `pageKey` (added post book-model cutover) — the exact occurrence page the
 * user was looking at when they queued this mutation. Recurring items
 * (DAILY / WEEKLY) NEED this so a queue drained after midnight still lands
 * on the intended date/week. INDIVIDUAL items have exactly one page
 * ('single'), so pageKey is a no-op for them but harmless when supplied.
 *
 * Absence means "use today's page at drain time" — that's the only sane
 * fallback for legacy tick-in-passing UIs that don't know which page they
 * were on. Post-wipe every new producer supplies it explicitly.
 */
export type ChecklistTogglePayload = {
  workspaceId: string
  goalId: string
  taskId: string | null
  subTaskId: string | null
  checklistItemId: string
  isCompleted: boolean
  workflowStatus?: HelperChecklistWorkflowStatus
  actorId: string
  actorName: string
  assignedAt: string | null
  pageKey?: string | null
  actorRole?: MobileOutboxActorRole
}

export type ChecklistProofPayload = {
  workspaceId: string
  checklistItemId: string
  fileName: string
  contentType: string
  blobKey: string
  actorId: string
  actorName: string
  assignedAt: string | null
  pageKey?: string | null
  actorRole?: MobileOutboxActorRole
  fileId?: string
}

export type ChecklistCommentPayload = {
  workspaceId: string
  checklistItemId: string
  parentContainerId: string
  body: string
  actorId: string
  actorName: string
  assignedAt: string | null
  pageKey?: string | null
  actorRole?: MobileOutboxActorRole
}

export type ChecklistVoiceMemoPayload = {
  workspaceId: string
  checklistItemId: string
  blobKey: string
  durationMs: number
  actorId: string
  actorName: string
  assignedAt: string | null
  pageKey?: string | null
  actorRole?: MobileOutboxActorRole
  fileId?: string
}

export type WorkspaceActivityPayload = {
  workspaceId: string
  actorId: string
  actorName: string
  targetType: 'CHECKLIST'
  targetId: string
  targetTitle: string
  action: 'HELPER_BLOCKER_RAISED' | 'HELPER_REPLENISHMENT_REQUESTED'
  summary: string
  metadata: Record<string, unknown>
}

export type StockCountSuggestionDraftPayload = {
  stockItemId?: string
  sku: string
  barcode: string
  countedQuantity: number
  systemQuantityAtScan: number
  note?: string | null
}

export type StockCountRoundPayload = {
  workspaceId: string
  roundId: string
  actorId: string
  actorName: string
  suggestions: StockCountSuggestionDraftPayload[]
}

export type PendingExpenseIntakePayload = {
  workspaceId: string
  checklistItemId: string
  taskId: string | null
  fileName: string
  contentType: string
  blobKey: string
  amountMinor: number
  currencyCode: string
  category?: string | null
  note?: string | null
  actorId: string
  actorName: string
  assignedAt: string | null
  pageKey?: string | null
  actorRole?: MobileOutboxActorRole
  fileId?: string
}

export type StockScanSuggestionPayload = {
  workspaceId: string
  kind: 'SCAN' | 'COUNT' | 'RECEIVED'
  stockItemId: string
  sku?: string
  barcode?: string
  direction?: 'IN' | 'OUT'
  quantity?: number
  countedQuantity?: number
  systemQuantityAtScan?: number
  actorId: string
  actorName: string
  actorRole?: MobileOutboxActorRole
  taskId?: string | null
  transferId?: string | null
  roundId?: string | null
  locationId?: string | null
  groupKey?: string | null
  note?: string | null
  createdAtClient?: number
}

export interface EduBoardingPayload {
  workspaceId: string
  runId: string
  learnerId: string
  status: 'BOARDED' | 'NOT_HERE'
  clientMutationId: string
}

export interface EduAttendanceMarkPayload {
  workspaceId: string
  seriesId: string
  date: string
  marks: Array<{
    learnerId: string
    status: 'PRESENT' | 'ABSENT' | 'LATE' | 'ON_LEAVE'
  }>
  reason?: string
  clientMutationId: string
}

export interface TimeClockPayload {
  workspaceId: string
  action: 'START' | 'STOP'
  clientGeneratedId: string
  occurredAt: string
  kind?: 'CLOCK' | 'TIMER'
  nodeId?: string | null
  geo?: { latitude: number; longitude: number; accuracy?: number } | null
  device?: string | null
  actorId?: string
  actorRole?: MobileOutboxActorRole
}

export interface OpsDueRecordPayload extends SubmitRecordInput {
  clientMutationId: string
}

export interface OpsCheckpointScanPayload extends RecordCheckpointScanInput {
  clientMutationId: string
}

export interface OpsSkipDuePayload extends SkipDueInput {
  clientMutationId: string
}

export interface OpsRaiseRequestPayload extends RaiseRequestInput {
  clientMutationId: string
}

export interface OpsAttendanceRollPayload extends MarkAttendanceRollInput {
  clientMutationId: string
}

export interface OpsStartRequestPayload extends StartRequestInput {
  clientMutationId: string
}

export interface OpsMarkRequestDonePayload extends MarkRequestDoneInput {
  clientMutationId: string
  blobKey?: string
  blobKeys?: string[]
}

export interface OpsBmwSavePayload extends SaveBmwDayInput {
  clientMutationId: string
}

export interface OpsDprLineBlob {
  lineId: string
  blobKey?: string
  blobKeys?: string[]
}

export interface OpsDprSavePayload extends SaveDprDraftInput {
  clientMutationId: string
  lineBlobs?: OpsDprLineBlob[]
}

export interface OpsConIssueCreatePayload extends CreateConIssueInput {
  clientMutationId: string
  blobKeys?: string[]
  createdIssueId?: string
  uploadedPhotoPaths?: string[]
}

export interface OpsConIssueAttachPhotosPayload {
  workspaceId: string
  issueId: string
  fileIds?: string[]
  blobKeys?: string[]
  title?: string
  clientMutationId?: string
  opId?: string
}

export type HelperMutationPayload =
  | { type: 'CHECKLIST_TOGGLE'; data: ChecklistTogglePayload }
  | { type: 'CHECKLIST_PROOF_UPLOAD'; data: ChecklistProofPayload }
  | { type: 'CHECKLIST_COMMENT'; data: ChecklistCommentPayload }
  | { type: 'CHECKLIST_VOICE_MEMO'; data: ChecklistVoiceMemoPayload }
  | { type: 'WORKSPACE_ACTIVITY'; data: WorkspaceActivityPayload }
  | { type: 'STOCK_COUNT_ROUND'; data: StockCountRoundPayload }
  | { type: 'PENDING_EXPENSE_INTAKE'; data: PendingExpenseIntakePayload }
  | { type: 'STOCK_SCAN_SUGGESTION'; data: StockScanSuggestionPayload }
  | { type: 'EDU_BOARDING'; data: EduBoardingPayload }
  | { type: 'EDU_ATTENDANCE_MARK'; data: EduAttendanceMarkPayload }
  | { type: 'TIME_CLOCK'; data: TimeClockPayload }
  | { type: 'OPS_DUE_RECORD'; data: OpsDueRecordPayload }
  | { type: 'OPS_CHECKPOINT_SCAN'; data: OpsCheckpointScanPayload }
  | { type: 'OPS_SKIP_DUE'; data: OpsSkipDuePayload }
  | { type: 'OPS_RAISE_REQUEST'; data: OpsRaiseRequestPayload }
  | { type: 'OPS_ATTENDANCE_ROLL'; data: OpsAttendanceRollPayload }
  | { type: 'OPS_START_REQUEST'; data: OpsStartRequestPayload }
  | { type: 'OPS_MARK_REQUEST_DONE'; data: OpsMarkRequestDonePayload }
  | { type: 'OPS_BMW_SAVE'; data: OpsBmwSavePayload }
  | { type: 'OPS_DPR_SAVE'; data: OpsDprSavePayload }
  | { type: 'OPS_CON_ISSUE_CREATE'; data: OpsConIssueCreatePayload }
  | { type: 'OPS_CON_ISSUE_ATTACH_PHOTOS'; data: OpsConIssueAttachPhotosPayload }

export interface HelperMutationRecord {
  id: string
  type: HelperMutationType
  payload: HelperMutationPayload['data']
  status: HelperMutationStatus
  retryCount: number
  error: string | null
  createdAt: number
  lastUpdatedClient: number
}

export interface HelperBlobRecord {
  key: string
  blob: Blob
  contentType: string
  createdAt: number
}

export class HelperOutboxUserUnboundError extends Error {
  constructor(message = 'Helper outbox operation refused: no user bound') {
    super(message)
    this.name = 'HelperOutboxUserUnboundError'
  }
}

const LEGACY_DB_NAME = 'helper-outbox'
const DB_VERSION = 1
const STORE_MUTATIONS = 'mutations'
const STORE_BLOBS = 'blobs'

let boundUid: string | null = null
let dbPromise: Promise<IDBDatabase> | null = null
let migrationRanForUid: string | null = null

export const getBoundHelperOutboxUser = (): string | null => boundUid

export const migrateLegacyOutboxForUser = async (uid: string): Promise<void> => {
  if (typeof indexedDB === 'undefined') return

  let legacyDb: IDBDatabase | null
  try {
    legacyDb = await new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(LEGACY_DB_NAME)
      req.onsuccess = () => {
        const db = req.result
        if (!db.objectStoreNames.contains(STORE_MUTATIONS)) {
          db.close()
          resolve(null as unknown as IDBDatabase)
          return
        }
        resolve(db)
      }
      req.onerror = () => reject(req.error)
    })
  } catch {
    return
  }

  if (!legacyDb) return

  try {
    const tx = legacyDb.transaction([STORE_MUTATIONS, STORE_BLOBS], 'readonly')
    const mutationsStore = tx.objectStore(STORE_MUTATIONS)
    const blobsStore = tx.objectStore(STORE_BLOBS)

    const allMutations = await new Promise<HelperMutationRecord[]>((res, rej) => {
      const r = mutationsStore.getAll()
      r.onsuccess = () => res(r.result as HelperMutationRecord[])
      r.onerror = () => rej(r.error)
    })

    const userMutations = allMutations.filter((m) => {
      const p = m.payload as Record<string, unknown> | undefined
      return p?.actorId === uid || p?.userId === uid
    })

    if (userMutations.length > 0) {
      const blobKeys: string[] = []
      for (const m of userMutations) {
        const p = m.payload as Record<string, unknown> | undefined
        if (typeof p?.blobKey === 'string') {
          blobKeys.push(p.blobKey)
        }
        if (Array.isArray(p?.blobKeys)) {
          for (const k of p.blobKeys) {
            if (typeof k === 'string') blobKeys.push(k)
          }
        }
      }

      const userBlobs: HelperBlobRecord[] = []
      for (const key of blobKeys) {
        const blobRecord = await new Promise<HelperBlobRecord | undefined>((res) => {
          const r = blobsStore.get(key)
          r.onsuccess = () => res(r.result as HelperBlobRecord | undefined)
          r.onerror = () => res(undefined)
        })
        if (blobRecord) userBlobs.push(blobRecord)
      }

      for (const m of userMutations) {
        await putMutation(m)
      }
      for (const b of userBlobs) {
        await putBlob(b)
      }

      legacyDb.close()
      const writeLegacyDb = await new Promise<IDBDatabase>((resolve, reject) => {
        const req = indexedDB.open(LEGACY_DB_NAME)
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
      })

      const deleteTx = writeLegacyDb.transaction([STORE_MUTATIONS, STORE_BLOBS], 'readwrite')
      const delMutStore = deleteTx.objectStore(STORE_MUTATIONS)
      const delBlobStore = deleteTx.objectStore(STORE_BLOBS)
      for (const m of userMutations) {
        delMutStore.delete(m.id)
      }
      for (const b of userBlobs) {
        delBlobStore.delete(b.key)
      }

      await new Promise<void>((resolve, reject) => {
        deleteTx.oncomplete = () => resolve()
        deleteTx.onerror = () => reject(deleteTx.error)
      })

      const checkTx = writeLegacyDb.transaction(STORE_MUTATIONS, 'readonly')
      const countReq = checkTx.objectStore(STORE_MUTATIONS).count()
      const remainingCount = await new Promise<number>((resolve) => {
        countReq.onsuccess = () => resolve(countReq.result)
        countReq.onerror = () => resolve(1)
      })
      writeLegacyDb.close()

      if (remainingCount === 0) {
        try {
          indexedDB.deleteDatabase(LEGACY_DB_NAME)
        } catch {
          // ignore
        }
      }
      return
    }

    legacyDb.close()
    if (allMutations.length === 0) {
      try {
        indexedDB.deleteDatabase(LEGACY_DB_NAME)
      } catch {
        // ignore
      }
    }
  } catch (err) {
    console.warn('[helperOutboxDb] Migration from legacy DB encountered error:', err)
    if (legacyDb) {
      try { legacyDb.close() } catch { /* ignore */ }
    }
  }
}

export const bindHelperOutboxUser = async (uid: string | null): Promise<void> => {
  if (boundUid === uid) return
  if (dbPromise) {
    try {
      const db = await dbPromise
      db.close()
    } catch {
      // ignore
    }
    dbPromise = null
  }
  boundUid = uid
  if (!uid) return

  if (migrationRanForUid !== uid) {
    migrationRanForUid = uid
    await migrateLegacyOutboxForUser(uid)
  }
}

const openDb = (): Promise<IDBDatabase> => {
  if (!boundUid) {
    return Promise.reject(new HelperOutboxUserUnboundError())
  }
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB unavailable'))
  }
  if (dbPromise) return dbPromise
  const dbName = `helper-outbox-${boundUid}`
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(dbName, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_MUTATIONS)) {
        const store = db.createObjectStore(STORE_MUTATIONS, { keyPath: 'id' })
        store.createIndex('status', 'status', { unique: false })
        store.createIndex('createdAt', 'createdAt', { unique: false })
      }
      if (!db.objectStoreNames.contains(STORE_BLOBS)) {
        db.createObjectStore(STORE_BLOBS, { keyPath: 'key' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IDB open failed'))
  })
  return dbPromise
}

const runTransaction = async <T>(
  storeName: typeof STORE_MUTATIONS | typeof STORE_BLOBS,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T> | Promise<T>,
): Promise<T> => {
  const db = await openDb()
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(storeName, mode)
    const store = tx.objectStore(storeName)
    const result = work(store)
    tx.onerror = () => reject(tx.error ?? new Error('IDB transaction failed'))
    tx.onabort = () => reject(tx.error ?? new Error('IDB transaction aborted'))
    if (result instanceof Promise) {
      result.then(resolve).catch(reject)
      return
    }
    result.onsuccess = () => resolve(result.result)
    result.onerror = () => reject(result.error ?? new Error('IDB request failed'))
  })
}

export const putMutation = async (record: HelperMutationRecord): Promise<void> => {
  await runTransaction(STORE_MUTATIONS, 'readwrite', (store) => store.put(record))
}

export const getMutation = async (id: string): Promise<HelperMutationRecord | undefined> => {
  return runTransaction(STORE_MUTATIONS, 'readonly', (store) =>
    store.get(id) as IDBRequest<HelperMutationRecord | undefined>,
  )
}

export const deleteMutation = async (id: string): Promise<void> => {
  await runTransaction(STORE_MUTATIONS, 'readwrite', (store) => store.delete(id))
}

export const listMutations = async (): Promise<HelperMutationRecord[]> => {
  return runTransaction(STORE_MUTATIONS, 'readonly', (store) =>
    store.getAll() as IDBRequest<HelperMutationRecord[]>,
  )
}

export const listPendingMutations = async (): Promise<HelperMutationRecord[]> => {
  const all = await listMutations()
  return all
    .filter((entry) => entry.status === 'PENDING' || entry.status === 'SYNCING')
    .sort((a, b) => a.createdAt - b.createdAt)
}

export const putBlob = async (record: HelperBlobRecord): Promise<void> => {
  await runTransaction(STORE_BLOBS, 'readwrite', (store) => store.put(record))
}

export const getBlob = async (key: string): Promise<HelperBlobRecord | undefined> => {
  return runTransaction(STORE_BLOBS, 'readonly', (store) =>
    store.get(key) as IDBRequest<HelperBlobRecord | undefined>,
  )
}

export const deleteBlob = async (key: string): Promise<void> => {
  await runTransaction(STORE_BLOBS, 'readwrite', (store) => store.delete(key))
}

export const clearAll = async (): Promise<void> => {
  await runTransaction(STORE_MUTATIONS, 'readwrite', (store) => store.clear())
  await runTransaction(STORE_BLOBS, 'readwrite', (store) => store.clear())
}

export const clearHelperOutboxForWorkspace = async (workspaceId: string): Promise<void> => {
  if (typeof indexedDB === 'undefined' || !boundUid) return
  try {
    const all = await listMutations()
    const matching = all.filter((entry) => (entry.payload as { workspaceId?: string })?.workspaceId === workspaceId)
    for (const m of matching) {
      await deleteMutation(m.id)
    }
  } catch {
    // ignore
  }
}

export const markHelperOutboxWorkspaceAccessLost = async (
  workspaceId: string,
  message?: string,
): Promise<void> => {
  if (typeof indexedDB === 'undefined' || !boundUid) return
  try {
    const all = await listMutations()
    const matching = all.filter(
      (entry) =>
        (entry.payload as { workspaceId?: string })?.workspaceId === workspaceId &&
        entry.status !== 'COMPLETED' &&
        entry.status !== 'ACCESS_LOST',
    )
    for (const m of matching) {
      await putMutation({
        ...m,
        status: 'ACCESS_LOST',
        error: message ?? m.error,
        lastUpdatedClient: Date.now(),
      })
    }
  } catch {
    // ignore
  }
}

export const clearHelperOutboxForUser = async (uid: string): Promise<void> => {
  if (typeof indexedDB === 'undefined') return
  const currentBound = boundUid
  try {
    await bindHelperOutboxUser(uid)
    await clearAll()
  } catch {
    try {
      indexedDB.deleteDatabase(`helper-outbox-${uid}`)
    } catch {
      // ignore
    }
  } finally {
    if (currentBound !== uid) {
      await bindHelperOutboxUser(currentBound)
    }
  }
}

export const expireOldHelperOutboxEntries = async (
  maxAgeMs: number,
  errorMessage = 'Update expired: device was offline longer than the allowed policy window.',
  now = Date.now(),
): Promise<number> => {
  if (typeof indexedDB === 'undefined' || !boundUid) return 0
  let expiredCount = 0
  try {
    const all = await listMutations()
    for (const m of all) {
      if (m.status !== 'COMPLETED' && m.status !== 'EXPIRED') {
        const itemTime = m.createdAt || m.lastUpdatedClient || now
        if (now - itemTime > maxAgeMs) {
          await putMutation({
            ...m,
            status: 'EXPIRED',
            error: errorMessage,
            lastUpdatedClient: now,
          })
          expiredCount++
        }
      }
    }
  } catch {
    // ignore
  }
  return expiredCount
}

export const clearAllHelperOutbox = async (): Promise<void> => {
  if (typeof indexedDB === 'undefined') return
  try {
    if (boundUid) {
      await clearAll()
    }
  } catch {
    // ignore
  }
}
